import type { Ytd } from "./api";

// ─── Period windows ──────────────────────────────────────────────────
//
// The API returns full-year monthly arrays; every period preset is just a
// month window summed client-side. "Closed" presets run through the last
// month-end (the accounting close); YTD/QTD fold in the in-flight month.

export type BvaPeriod = "CLOSED_YTD" | "CLOSED_QTD" | "YTD" | "QTD" | "LAST_MONTH" | "CUSTOM";

export const PERIOD_OPTIONS: { value: BvaPeriod; label: string }[] = [
  { value: "CLOSED_YTD", label: "YTD (Closed)" },
  { value: "CLOSED_QTD", label: "QTD (Closed)" },
  { value: "YTD", label: "YTD" },
  { value: "QTD", label: "QTD" },
  { value: "LAST_MONTH", label: "Last Month" },
  { value: "CUSTOM", label: "Custom" },
];

/** 0-based inclusive month indices. `end < start` = empty window. */
export interface MonthWindow {
  start: number;
  end: number;
}

/**
 * Resolve a preset to a month window given how many months have closed.
 * `completeMonths` is the API's count of complete months (0–12); the
 * in-flight month, when there is one, is index `completeMonths`.
 */
export function periodWindow(
  period: BvaPeriod,
  completeMonths: number,
  custom?: MonthWindow | null,
): MonthWindow {
  const lastClosed = completeMonths - 1; // -1 = nothing closed yet
  const current = Math.min(completeMonths, 11);
  const quarterStart = (m: number) => Math.floor(m / 3) * 3;
  switch (period) {
    case "CLOSED_YTD":
      return { start: 0, end: lastClosed };
    case "CLOSED_QTD":
      return lastClosed < 0
        ? { start: 0, end: -1 }
        : { start: quarterStart(lastClosed), end: lastClosed };
    case "YTD":
      return { start: 0, end: current };
    case "QTD":
      return { start: quarterStart(current), end: current };
    case "LAST_MONTH":
      return { start: lastClosed, end: lastClosed };
    case "CUSTOM":
      return custom ?? { start: 0, end: lastClosed };
  }
}

/** Sum a block's monthly series over a window into a Ytd-shaped aggregate.
 *  Structural param: works for class, category, and account blocks alike. */
export function windowTotals(
  block: { monthly: { budget: number[]; actual: number[] } },
  w: MonthWindow,
): Ytd {
  let budget = 0;
  let actual = 0;
  for (let i = Math.max(0, w.start); i <= Math.min(w.end, 11); i++) {
    budget += block.monthly.budget[i] ?? 0;
    actual += block.monthly.actual[i] ?? 0;
  }
  budget = Math.round(budget * 100) / 100;
  actual = Math.round(actual * 100) / 100;
  const variance = Math.round((actual - budget) * 100) / 100;
  return {
    budget,
    actual,
    variance,
    pct: budget ? Math.round(((actual - budget) / budget) * 1000) / 10 : null,
  };
}

/** The chart span for a preset: budget draws to its natural period end
 *  (year for YTD-ish, quarter for QTD-ish), actuals stop at the window end. */
export function chartWindow(period: BvaPeriod, w: MonthWindow): MonthWindow & { budgetEnd: number } {
  switch (period) {
    case "CLOSED_YTD":
    case "YTD":
      return { start: 0, end: w.end, budgetEnd: 11 };
    case "CLOSED_QTD":
    case "QTD":
      return { start: w.start, end: w.end, budgetEnd: Math.floor(w.start / 3) * 3 + 2 };
    default:
      return { start: w.start, end: w.end, budgetEnd: w.end };
  }
}

/** Human label for a resolved window, e.g. "Jan–Jul" or "Jul". */
export function windowLabel(w: MonthWindow): string {
  if (w.end < w.start || w.start < 0) return "no closed months yet";
  const a = MONTH_LABELS[Math.max(0, w.start)];
  const b = MONTH_LABELS[Math.min(w.end, 11)];
  return a === b ? (a as string) : `${a}–${b}`;
}

/** Running sum of a monthly series, cut off after `upto` months (0 = none). */
export function cumulative(values: number[], upto: number): number[] {
  const out: number[] = [];
  let sum = 0;
  for (let i = 0; i < Math.min(upto, values.length); i++) {
    sum += values[i] ?? 0;
    out.push(Math.round(sum * 100) / 100);
  }
  return out;
}

/** Spend % of budget for the progress meter (clamped for rendering; a class
 *  with no budget but real spend reads as fully over). */
export function spendPct(t: Ytd): number {
  if (t.budget <= 0) return t.actual > 0 ? 100 : 0;
  return (t.actual / t.budget) * 100;
}

export function overBudget(t: Ytd): boolean {
  return t.variance > 0;
}

/** True when there's spend but no budget line at all (e.g. Loyalty). */
export function unbudgeted(t: Ytd, fyBudget: number): boolean {
  return t.budget === 0 && fyBudget === 0 && t.actual > 0;
}

export const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;
